import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { ProfileT } from "@patient-watch/types";
import { useAuth } from "@/utils/auth";
import { filterStaffBySecretaryOwnership, selectLinkedSecretaryIds } from "./filterStaffBySecretaryOwnership";
import { selectProfilesByRoles } from "./selectProfilesByRoles";

export interface TeamMember extends ProfileT {
    isFavourite: boolean;
    isInvitedByMe: boolean;
    canEditName: boolean;
    organisationName?: string;
    organisationId?: string;
    roleInOrg?: string;
}

export interface OrganisationGroup {
    organisationId: string;
    organisationName: string;
    members: TeamMember[];
}

export interface UserOrganisation {
    organisation_id: string;
    organisation_name: string;
    role: string;
}

export interface TeamMembersData {
    byOrganisation: OrganisationGroup[];
    all: TeamMember[];
    /** Unique staff memberships (one row per person + organisation) for the team table. */
    members: TeamMember[];
    favourites: TeamMember[];
    invitedByMe: TeamMember[];
    currentUserOrganisations: UserOrganisation[];
    counts: {
        total: number;
        favourites: number;
        invitedByMe: number;
        organisations: number;
    };
}

async function selectTeamMembers(userId: string): Promise<TeamMembersData> {
    const currentUserResult = await supabaseClient
        .from("profiles")
        .select("organisations, is_super_user")
        .eq("user_id", userId)
        .single();

    if (currentUserResult.error) throw currentUserResult.error;

    const currentUserOrgs =
        (currentUserResult.data?.organisations as Array<{
            organisation_id: string;
            organisation_name: string;
            role: string;
        }>) || [];
    const currentUserOrgIds = new Set(currentUserOrgs.map((o) => o.organisation_id));
    const skipSecretaryFilter = Boolean(currentUserResult.data?.is_super_user);

    const [staffResult, favouritesResult, invitedResult, linkedSecretaryIds] = await Promise.all([
        selectProfilesByRoles({ roles: ["CLINICIAN", "SECRETARY", "MANAGER", "ADMIN", "VALIDATOR", "AUDITOR"] }),
        supabaseClient.from("favourite_users").select("favourited_user").eq("favourited_by", userId),
        supabaseClient.from("profiles").select("*").eq("invited_by", userId),
        selectLinkedSecretaryIds(userId),
    ]);

    if (favouritesResult.error) throw favouritesResult.error;
    if (invitedResult.error) throw invitedResult.error;

    const staff = filterStaffBySecretaryOwnership(staffResult, linkedSecretaryIds, {
        skipFilter: skipSecretaryFilter,
    });
    const favouriteIds = new Set((favouritesResult.data || []).map((f) => f.favourited_user));
    const invitedUsers = invitedResult.data || [];
    const invitedIds = new Set(invitedUsers.map((u) => u.user_id));

    const allMembers: TeamMember[] = [];
    const uniqueUsers = new Map<string, TeamMember>();
    const seenMemberships = new Map<string, TeamMember>();
    const organisationMap = new Map<string, OrganisationGroup>();

    const addMembership = (
        baseUser: TeamMember,
        org: { organisation_id: string; organisation_name: string; role: string }
    ) => {
        const key = `${baseUser.user_id}:${org.organisation_id}`;
        const existing = seenMemberships.get(key);
        if (existing) {
            if (org.role === "SECRETARY") {
                existing.roleInOrg = "SECRETARY";
            }
            return;
        }

        const member: TeamMember = {
            ...baseUser,
            organisationId: org.organisation_id,
            organisationName: org.organisation_name,
            roleInOrg: org.role,
        };
        seenMemberships.set(key, member);
        allMembers.push(member);

        if (!organisationMap.has(org.organisation_id)) {
            organisationMap.set(org.organisation_id, {
                organisationId: org.organisation_id,
                organisationName: org.organisation_name,
                members: [],
            });
        }
        organisationMap.get(org.organisation_id)!.members.push(member);
    };

    for (const user of staff) {
        if (user.user_id && user.user_id !== userId) {
            const userOrgs =
                (user.organisations as Array<{ organisation_id: string; organisation_name: string; role: string }>) ||
                [];

            const sharedOrgs = userOrgs.filter(
                (org) => currentUserOrgIds.has(org.organisation_id) && org.role !== "PATIENT"
            );

            if (sharedOrgs.length === 0) continue;

            const baseUser = {
                ...user,
                isFavourite: favouriteIds.has(user.user_id),
                isInvitedByMe: invitedIds.has(user.user_id),
                canEditName: user.invited_by === userId,
            } as TeamMember;

            if (!uniqueUsers.has(user.user_id)) {
                uniqueUsers.set(user.user_id, baseUser);
            }

            for (const org of sharedOrgs) {
                addMembership(baseUser, org);
            }
        }
    }

    for (const user of invitedUsers) {
        if (user.user_id && user.user_id !== userId && !uniqueUsers.has(user.user_id)) {
            const roles = user.roles ?? [];
            const isSecretaryOnly =
                roles.includes("SECRETARY") &&
                !roles.some((r) => r === "CLINICIAN" || r === "ADMIN" || r === "MANAGER");
            if (isSecretaryOnly && !skipSecretaryFilter && !linkedSecretaryIds.has(user.user_id)) {
                continue;
            }

            const userOrgs =
                (user.organisations as Array<{ organisation_id: string; organisation_name: string; role: string }>) ||
                [];
            const sharedOrgs = userOrgs.filter(
                (org) => currentUserOrgIds.has(org.organisation_id) && org.role !== "PATIENT"
            );

            if (sharedOrgs.length === 0) continue;

            const baseUser = {
                ...user,
                isFavourite: favouriteIds.has(user.user_id),
                isInvitedByMe: true,
                canEditName: true,
            } as TeamMember;
            uniqueUsers.set(user.user_id, baseUser);

            for (const org of sharedOrgs) {
                addMembership(baseUser, org);
            }
        }
    }

    const sortMembers = (members: TeamMember[]) => {
        return members.sort((a, b) => {
            const orgA = (a.organisationName || "").toLowerCase();
            const orgB = (b.organisationName || "").toLowerCase();
            const orgCompare = orgA.localeCompare(orgB);
            if (orgCompare !== 0) return orgCompare;
            const nameA = `${a.first_name || ""} ${a.last_name || ""}`.trim().toLowerCase();
            const nameB = `${b.first_name || ""} ${b.last_name || ""}`.trim().toLowerCase();
            return nameA.localeCompare(nameB);
        });
    };

    const byOrganisation = Array.from(organisationMap.values())
        .sort((a, b) => a.organisationName.localeCompare(b.organisationName))
        .map((org) => ({
            ...org,
            members: sortMembers(org.members),
        }));

    const uniqueUsersList = sortMembers(Array.from(uniqueUsers.values()));
    const membershipRows = sortMembers(allMembers);
    const favourites = uniqueUsersList.filter((u) => u.isFavourite);
    const invitedByMe = uniqueUsersList.filter((u) => u.isInvitedByMe);

    return {
        byOrganisation,
        all: membershipRows,
        members: membershipRows,
        favourites,
        invitedByMe,
        currentUserOrganisations: currentUserOrgs,
        counts: {
            total: uniqueUsersList.length,
            favourites: favourites.length,
            invitedByMe: invitedByMe.length,
            organisations: byOrganisation.length,
        },
    };
}

export const selectTeamMembersQueryOptions = (userId: string) =>
    queryOptions({
        queryKey: ["team-members", userId],
        queryFn: () => selectTeamMembers(userId),
        enabled: Boolean(userId),
    });

export function useSelectTeamMembers() {
    const auth = useAuth();
    return useQuery(selectTeamMembersQueryOptions(auth.id));
}
